import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const MatchingArea = ({ 
  draggedItem, 
  draggedSource, 
  onDrop, 
  onMatch, 
  onUnmatch,
  selectedBankTransactions,
  selectedLedgerEntries,
  bankTransactions,
  ledgerEntries
}) => {
  const handleDragOver = (e) => {
    e?.preventDefault();
  };

  const getSelectedBankTotal = () => {
    return selectedBankTransactions?.reduce((sum, id) => {
      const transaction = bankTransactions?.find(t => t?.id === id);
      return sum + (transaction ? transaction?.amount : 0);
    }, 0);
  };

  const getSelectedLedgerTotal = () => {
    return selectedLedgerEntries?.reduce((sum, id) => {
      const entry = ledgerEntries?.find(e => e?.id === id);
      return sum + (entry ? entry?.amount : 0);
    }, 0);
  };

  const bankTotal = getSelectedBankTotal();
  const ledgerTotal = getSelectedLedgerTotal();
  const difference = Math.abs(bankTotal - ledgerTotal);
  const isBalanced = difference < 0.01;

  const canMatch = selectedBankTransactions?.length > 0 && 
                   selectedLedgerEntries?.length > 0 && 
                   isBalanced;

  return (
    <div 
      className="bg-card rounded-lg shadow-sm border-2 border-dashed border-border p-4 md:p-6 transition-smooth"
      onDragOver={handleDragOver}
      onDrop={onDrop}
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="ArrowLeftRight" size={32} color="var(--color-accent)" />
        </div>
        <h3 className="font-heading font-semibold text-lg md:text-xl mb-2">Matching Area</h3>
        <p className="caption text-muted-foreground">
          Drag transactions here or select items to match
        </p>
      </div>
      {(selectedBankTransactions?.length > 0 || selectedLedgerEntries?.length > 0) && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Building2" size={16} color="var(--color-primary)" />
                <span className="caption font-medium text-primary">Bank Transactions</span>
              </div>
              <p className="data-text text-xl md:text-2xl font-semibold text-foreground">
                ${bankTotal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="caption text-muted-foreground mt-1">
                {selectedBankTransactions?.length} selected
              </p>
            </div>

            <div className="bg-secondary/5 rounded-lg p-4 border border-secondary/20">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="BookOpen" size={16} color="var(--color-secondary)" />
                <span className="caption font-medium text-secondary">Ledger Entries</span>
              </div>
              <p className="data-text text-xl md:text-2xl font-semibold text-foreground">
                ${ledgerTotal?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="caption text-muted-foreground mt-1">
                {selectedLedgerEntries?.length} selected
              </p>
            </div>
          </div>

          <div className={`
            rounded-lg p-4 border-2
            ${isBalanced 
              ? 'bg-success/5 border-success/20' :'bg-warning/5 border-warning/20'
            }
          `}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon 
                  name={isBalanced ? 'CheckCircle' : 'AlertTriangle'} 
                  size={20} 
                  color={isBalanced ? 'var(--color-success)' : 'var(--color-warning)'} 
                />
                <span className="font-body font-medium text-sm md:text-base">
                  {isBalanced ? 'Amounts Match' : 'Difference'}
                </span>
              </div>
              <span className={`
                data-text text-lg md:text-xl font-semibold
                ${isBalanced ? 'text-success' : 'text-warning'}
              `}>
                ${difference?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="default"
              onClick={onMatch}
              disabled={!canMatch}
              iconName="Link"
              iconPosition="left"
              fullWidth
            >
              Match Transactions
            </Button>
            <Button
              variant="outline"
              onClick={onUnmatch}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>
      )}
      {selectedBankTransactions?.length === 0 && selectedLedgerEntries?.length === 0 && (
        <div className="text-center py-8">
          <p className="caption text-muted-foreground">
            Select or drag transactions to begin matching
          </p>
        </div>
      )}
    </div>
  );
};

export default MatchingArea;